import { AppDispatch, RootState } from '@/stores'
import { updateComponentAction } from '@/stores/active-design'
import { LoadingSpinner, ToggleButtonGroup } from '@cfs-fixings/shared'
import { useDispatch, useSelector } from 'react-redux'
import PanelTitle from './components/PanelTitle'
import PanelSections from './components/PanelSections'
import PanelSection from './components/PanelSection'
import { useGetSubstratesQuery } from '@/api/serverApi'
import * as v from 'valibot'
import { useEffect, useState } from 'react'
import previewImage from '@/assets/concrete_slab_preview.png'
import PanelInput from './components/PanelInput'
import useChannelFixingPosition from '@/hooks/useChannelFixingPosition'
import GeometryDiagram from './geometry-diagram/GeometryDiagram'
import EdgeDistanceToggle from './components/EdgeDistanceToggle'
import { EdgeDistanceToggleType } from '@/enums/EdgeDistanceToggleType'
import PanelCheckbox from './components/PanelCheckbox'
import PanelSelect from './components/PanelSelect'
import { EdgeReinforcementType } from '@/enums/EdgeReinforcementType'

type TriggerUpdateKey = keyof NonNullable<Parameters<typeof updateComponentAction>[0]['details']['concrete_slab']>

const concreteSchema = v.object({
  cracked: v.optional(v.boolean()),
  thickness: v.optional(v.number([v.integer(), v.minValue(0)])),
  edge_distance_xpos: v.optional(v.number([v.integer(), v.minValue(0)])),
  edge_distance_xneg: v.optional(v.number([v.integer(), v.minValue(0)])),
  edge_distance_ypos: v.optional(v.number([v.integer(), v.minValue(0)])),
  edge_distance_yneg: v.optional(v.number([v.integer(), v.minValue(0)])),
  is_thickness_infinite: v.optional(v.boolean()),
  is_edge_distance_x_pos_infinite: v.optional(v.boolean()),
  is_edge_distance_x_neg_infinite: v.optional(v.boolean()),
  is_edge_distance_y_pos_infinite: v.optional(v.boolean()),
  is_edge_distance_y_neg_infinite: v.optional(v.boolean()),
  has_splitting_reinforcement: v.optional(v.boolean()),
  has_widely_spaced_reinforcement: v.optional(v.boolean()),
  has_dense_reinforcement: v.optional(v.boolean()),
  edge_reinforcement_type: v.optional(v.enum_(EdgeReinforcementType)),
})

const ConcreteProperties = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [errorKeys, setErrorKeys] = useState<string[]>([])
  const [cracked, setCracked] = useState(true)

  const [hasSplittingReinforcement, setHasSplittingReinforcement] = useState(false)
  const [hasWidelySpacedReinforcement, setHasWidelySpacedReinforcement] = useState(false)
  const [hasDenseReinforcement, setHasDenseReinforcement] = useState(false)
  const [hasEdgeReinforcement, setHasEdgeReinforcement] = useState(false)
  const [edgeReinforcementType, setEdgeReinforcementType] = useState(EdgeReinforcementType.StraightEdge)

  const { fixingPosition } = useChannelFixingPosition()

  const selectedComponentId = useSelector((state: RootState) => state.activeDesign.selectedComponentId)

  const component = useSelector((state: RootState) => state.activeDesign.design?.components?.find(c => c.id === selectedComponentId))
  const channel = useSelector((state: RootState) => state.activeDesign.design?.components?.find(c => c.cast_in_anchor_channel))

  useEffect(() => {
    if (component?.concrete_slab?.cracked) {
      setCracked(component.concrete_slab.cracked)
    }

    if (component?.concrete_slab?.has_splitting_reinforcement) {
      setHasSplittingReinforcement(component.concrete_slab.has_splitting_reinforcement)
    }

    if (component?.concrete_slab?.has_widely_spaced_reinforcement) {
      setHasWidelySpacedReinforcement(component.concrete_slab.has_widely_spaced_reinforcement)
    }

    if (component?.concrete_slab?.has_dense_reinforcement) {
      setHasDenseReinforcement(component.concrete_slab.has_dense_reinforcement)
    }

    if (component?.concrete_slab?.edge_reinforcement_type) {
      setEdgeReinforcementType(component.concrete_slab.edge_reinforcement_type as EdgeReinforcementType)

      if ((component.concrete_slab.edge_reinforcement_type as EdgeReinforcementType) !== EdgeReinforcementType.None) {
        setHasEdgeReinforcement(true)
      }
    }
  }, [component])

  const { data: substrates, isFetching } = useGetSubstratesQuery()

  const updateCracked = (value: boolean) => {
    setCracked(value)
    triggerUpdate('cracked', value)
  }

  const updateHasSplittingReinforcement = (value: boolean) => {
    setHasSplittingReinforcement(value)
    triggerUpdate('has_splitting_reinforcement', value)
  }

  const updateHasWidelySpacedReinforcement = async (value: boolean) => {
    setHasWidelySpacedReinforcement(value)
    triggerUpdate('has_widely_spaced_reinforcement', value)

    // We set the value in state here, but trying to also send a second update
    // causes a race condition, so we do it in the triggerUpdate call
    if (value) {
      setHasDenseReinforcement(false)
    }
  }

  const updateHasDenseReinforcement = async (value: boolean) => {
    setHasDenseReinforcement(value)
    triggerUpdate('has_dense_reinforcement', value)

    // We set the value in state here, but trying to also send a second update
    // causes a race condition, so we do it in the triggerUpdate call
    if (value) {
      setHasWidelySpacedReinforcement(false)
    }
  }

  const updateHasEdgeReinforcement = (value: boolean) => {
    setHasEdgeReinforcement(value)

    if (!value) {
      updateEdgeReinforcement(EdgeReinforcementType.None)
    } else {
      updateEdgeReinforcement(EdgeReinforcementType.StraightEdge)
    }
  }

  const updateEdgeReinforcement = (value: EdgeReinforcementType) => {
    setEdgeReinforcementType(value)
    triggerUpdate('edge_reinforcement_type', value)
  }

  const triggerUpdate = async (key: TriggerUpdateKey, value: any) => {
    const vResult = v.safeParse(concreteSchema, { [key]: value })

    if (!vResult.success) {
      setErrorKeys([
        ...errorKeys,
        ...vResult.issues.map(i => (
          i.path!.reduce((acc, curr) => [...acc, curr.key as string], [] as string[]).join('.')
        )),
      ])
      return
    }

    setErrorKeys(errorKeys.filter(k => k !== key))

    // Manually append an additional key and value if the widely spaced or dense
    // reinforcement are set to enforce that only one of them can be set at a time
    let additionalValues = {}

    if (key === 'has_widely_spaced_reinforcement' && value === true) {
      additionalValues = { has_dense_reinforcement: false }
    } else if (key === 'has_dense_reinforcement' && value === true) {
      additionalValues = { has_widely_spaced_reinforcement: false }
    }

    dispatch(updateComponentAction({
      componentId: selectedComponentId!,
      details: {
        concrete_slab: {
          [key]: value,
          ...additionalValues,
        },
      },
    }))
  }

  if (isFetching) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner className="w-4 h-4" />
      </div>
    )
  }

  return (
    <>
      <PanelTitle>Substrate</PanelTitle>

      <PanelSections>
        <PanelSection>
          <div className="flex gap-[10px] items-center">
            <img
              src={previewImage}
              alt=""
              className="w-[47px] h-[47px] object-cover rounded-[2px]"
            />

            <h3 className="font-semibold text-[14px] text-gray-900">
              Slab - {component?.concrete_slab?.strength}
            </h3>
          </div>
        </PanelSection>

        <PanelSection title="Concrete Strength">
          <div className="space-y-[20px]">
            <PanelSelect onChange={e => triggerUpdate('strength', e.target.value)} defaultValue={component?.concrete_slab?.strength}>
              {
                substrates?.data.concrete.strengths.map(strength => (
                  <option key={strength} value={strength}>{strength}</option>
                ))
              }
            </PanelSelect>
            <ToggleButtonGroup>
              <ToggleButtonGroup.Button
                selected={cracked}
                onClick={() => updateCracked(true)}
              >
                Cracked
              </ToggleButtonGroup.Button>
              <ToggleButtonGroup.Button
                selected={!cracked}
                onClick={() => updateCracked(false)}
              >
                Uncracked
              </ToggleButtonGroup.Button>
            </ToggleButtonGroup>
          </div>
        </PanelSection>

        {
          channel
          && (
            <PanelSection title="Geometry">
              <div className="space-y-[20px]">
                <GeometryDiagram />
                <div className="space-y-[10px]">
                  <PanelInput
                    defaultValue={component?.concrete_slab?.thickness}
                    title="Thickness (h)"
                    onBlur={e => triggerUpdate('thickness', parseInt(e.target.value))}
                    disabled={!!(component?.concrete_slab?.is_thickness_infinite ?? false)}
                    preventKeys={['Minus']}
                    error={errorKeys.includes('thickness')}
                  />
                  <PanelInput
                    defaultValue={component?.concrete_slab?.edge_distance_x_neg}
                    title="Edge distance (e2,1)"
                    onBlur={e => triggerUpdate('edge_distance_x_neg', parseInt(e.target.value))}
                    disabled={!!(component?.concrete_slab?.is_edge_distance_x_neg_infinite ?? false)}
                    preventKeys={['Minus']}
                    error={errorKeys.includes('edge_distance_x_neg')}
                  />
                  <PanelInput
                    defaultValue={component?.concrete_slab?.edge_distance_x_pos}
                    title="Edge distance (e2,2)"
                    onBlur={e => triggerUpdate('edge_distance_x_pos', parseInt(e.target.value))}
                    disabled={!!(component?.concrete_slab?.is_edge_distance_x_pos_infinite ?? false)}
                    preventKeys={['Minus']}
                    error={errorKeys.includes('edge_distance_x_pos')}
                  />
                  <PanelInput
                    defaultValue={component?.concrete_slab?.edge_distance_y_pos}
                    title={fixingPosition === 'top' ? 'Edge distance (c1,2)' : 'Edge distance (c1,2)'}
                    onBlur={e => triggerUpdate('edge_distance_y_pos', parseInt(e.target.value))}
                    disabled={!!(component?.concrete_slab?.is_edge_distance_y_pos_infinite ?? false)}
                    preventKeys={['Minus']}
                    error={errorKeys.includes('edge_distance_y_pos')}
                  />
                  <PanelInput
                    defaultValue={component?.concrete_slab?.edge_distance_y_neg}
                    title={fixingPosition === 'top' ? 'Edge distance (c1,1)' : 'Edge distance (c1,1)'}
                    onBlur={e => triggerUpdate('edge_distance_y_neg', parseInt(e.target.value))}
                    disabled={!!(component?.concrete_slab?.is_edge_distance_y_neg_infinite ?? false)}
                    preventKeys={['Minus']}
                    error={errorKeys.includes('edge_distance_y_neg')}
                  />
                </div>
              </div>
            </PanelSection>
          )
        }

        {
          channel
          && (
            <PanelSection title="Edges">
              <div className="space-y-[20px]">
                <EdgeDistanceToggle
                  values={{
                    positiveY: !!(component?.concrete_slab?.is_edge_distance_y_pos_infinite ?? false),
                    negativeY: !!(component?.concrete_slab?.is_edge_distance_y_neg_infinite ?? false),
                    positiveX: !!(component?.concrete_slab?.is_edge_distance_x_pos_infinite ?? false),
                    negativeX: !!(component?.concrete_slab?.is_edge_distance_x_neg_infinite ?? false),
                    thickness: !!(component?.concrete_slab?.is_thickness_infinite ?? false),
                  }}
                  onToggle={(value, type) => {
                    switch (type) {
                      case EdgeDistanceToggleType.PositiveY:
                        triggerUpdate('is_edge_distance_y_pos_infinite', value)
                        break
                      case EdgeDistanceToggleType.NegativeY:
                        triggerUpdate('is_edge_distance_y_neg_infinite', value)
                        break
                      case EdgeDistanceToggleType.PositiveX:
                        triggerUpdate('is_edge_distance_x_pos_infinite', value)
                        break
                      case EdgeDistanceToggleType.NegativeX:
                        triggerUpdate('is_edge_distance_x_neg_infinite', value)
                        break
                      case EdgeDistanceToggleType.Thickness:
                        triggerUpdate('is_thickness_infinite', value)
                        break
                    }
                  }}
                />
              </div>
            </PanelSection>
          )
        }

        <PanelSection title="Reinforcement">
          <div className="space-y-[15px]">
            <PanelCheckbox
              name="splitting_reinforcement"
              checked={hasSplittingReinforcement}
              onChange={(e) => updateHasSplittingReinforcement(e.target.checked)}
            >
              Splitting reinforcement
            </PanelCheckbox>
            <PanelCheckbox
              name="widely_spaced_reinforcement"
              checked={hasWidelySpacedReinforcement}
              onChange={(e) => updateHasWidelySpacedReinforcement(e.target.checked)}
            >
              Widely spaced reinforcement
            </PanelCheckbox>
            <PanelCheckbox
              name="dense_reinforcement"
              checked={hasDenseReinforcement}
              onChange={(e) => updateHasDenseReinforcement(e.target.checked)}
            >
              Dense reinforcement
            </PanelCheckbox>
            <PanelCheckbox
              name="edge_reinforcement"
              checked={hasEdgeReinforcement}
              onChange={(e) => updateHasEdgeReinforcement(e.target.checked)}
            >
              Edge reinforcement
            </PanelCheckbox>
          </div>
        </PanelSection>
        {
          hasEdgeReinforcement
          && (
            <PanelSection>
              <div className="space-y-[15px]">
                <p className="text-gray-650 text-xs leading-[14.4px]">Edge reinforcement</p>
                <PanelSelect
                  value={edgeReinforcementType}
                  onChange={(e) => updateEdgeReinforcement(e.target.value as EdgeReinforcementType)}
                >
                  {
                    Object.values(EdgeReinforcementType).filter((e) => e !== EdgeReinforcementType.None).map((value) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {`${value[0].toUpperCase()}${value.substring(1).split('_').join(' ')}`}
                      </option>
                    ))
                  }
                </PanelSelect>
              </div>
              <div className="pb-6"></div>
            </PanelSection>
          )
        }
      </PanelSections>
    </>
  )
}

export default ConcreteProperties
